// Offline-aware intake submission queue using localStorage.
// Submissions are appended to a queue; a flush attempt happens immediately,
// and again whenever the browser regains connectivity.

import axios from "axios";

const STORAGE_KEY = "hr_intake_queue_v1";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function readQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    /* quota exceeded — surface to caller via return value of enqueue */
  }
}

function makeClientId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `hr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getPendingCount() {
  return readQueue().length;
}

export function enqueueSubmission(payload) {
  const queue = readQueue();
  const entry = {
    ...payload,
    client_id: payload.client_id || makeClientId(),
    submitted_at_client: payload.submitted_at_client || new Date().toISOString(),
  };
  queue.push(entry);
  writeQueue(queue);
  return entry;
}

async function postOne(entry) {
  return axios.post(`${API}/intake`, entry, { timeout: 15000 });
}

let flushing = false;

export async function flushQueue() {
  if (flushing) return { flushed: 0, remaining: getPendingCount() };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { flushed: 0, remaining: getPendingCount(), offline: true };
  }

  flushing = true;
  let flushed = 0;
  try {
    let queue = readQueue();
    while (queue.length > 0) {
      const next = queue[0];
      try {
        await postOne(next);
        queue = queue.slice(1);
        writeQueue(queue);
        flushed += 1;
      } catch (err) {
        // Network or server problem — stop and try again later.
        break;
      }
    }
    return { flushed, remaining: readQueue().length };
  } finally {
    flushing = false;
  }
}

export function installAutoFlush(onChange) {
  const notify = () => {
    if (typeof onChange === "function") {
      onChange({ pending: getPendingCount(), online: navigator.onLine !== false });
    }
  };

  const handleOnline = async () => {
    notify();
    await flushQueue();
    notify();
  };
  const handleOffline = () => notify();

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  // Initial attempt in case there are pending items from a previous session
  handleOnline();

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}
